import { queryOptions, useQuery } from "@tanstack/react-query";

import { blastHolesAjax } from "../../ajax/BlastHolesAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { DrillingEntryMode, DrillingSessionHolesDTO, HoleDipDTO, PatternChargingDTO, PatternHolesDTO } from "../../DTOs/DrillBlast/BlastHoleDTO";
import { queryClient } from "../../queryClient";

export const patternHolesQuery = (patternBlastId: number) => queryOptions({
  queryKey: ['drillBlast', 'patternHoles', patternBlastId],
  queryFn: async (): Promise<PatternHolesDTO> => {
    const res = await blastHolesAjax.getHoles(patternBlastId);
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the holes');
    return res;
  },
});

export const usePatternHoles = (patternBlastId: number) => useQuery(patternHolesQuery(patternBlastId));

// Note: a pattern added offline is not on the server yet, so its holes come from the pattern's saved plan with nothing
// drilled, and the result never goes stale so the attempts queued against it stay on screen until it syncs.
export const drillingSessionHolesQuery = (drillingBlastId: number, patternBlastId: number) => queryOptions({
  queryKey: ['drillBlast', 'drillingSessionHoles', drillingBlastId],
  ...(drillingBlastId < 0 ? { staleTime: Infinity } : {}),
  queryFn: async (): Promise<DrillingSessionHolesDTO> => {
    if (drillingBlastId < 0) {
      const holes = queryClient.getQueryData(patternHolesQuery(patternBlastId).queryKey)
        ?? await queryClient.fetchQuery(patternHolesQuery(patternBlastId));
      return { drillingBlastId, patternBlastId, reference: holes.reference, entryMode: DrillingEntryMode.Hole, canChangeMode: false, attempts: [], holes };
    }
    const res = await blastHolesAjax.getSession(drillingBlastId);
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the holes drilled');
    return res;
  },
});

export const useDrillingSessionHoles = (drillingBlastId: number, patternBlastId: number, enabled = true) =>
  useQuery({ ...drillingSessionHolesQuery(drillingBlastId, patternBlastId), enabled });

export const patternDipsQuery = (patternBlastId: number) => queryOptions({
  queryKey: ['drillBlast', 'patternDips', patternBlastId],
  queryFn: async (): Promise<HoleDipDTO[]> => {
    const res = await blastHolesAjax.getDips(patternBlastId);
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the dips');
    return res;
  },
});

export const usePatternDips = (patternBlastId: number) => useQuery(patternDipsQuery(patternBlastId));

export const patternChargingQuery = (patternBlastId: number) => queryOptions({
  queryKey: ['drillBlast', 'patternCharging', patternBlastId],
  queryFn: async (): Promise<PatternChargingDTO> => {
    const res = await blastHolesAjax.getCharging(patternBlastId);
    if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load the charging');
    return res;
  },
});

export const usePatternCharging = (patternBlastId: number) => useQuery(patternChargingQuery(patternBlastId));
